// Les mots de « Nouvelle demande » : « vous » quand on demande pour soi, le prénom de la personne côté gestion. Tant que la personne absente n'est pas
// choisie, on tourne la phrase sans sujet (ou on dit « la personne absente »).
// Un mot par notion : séance, remplaçant·e, À rechercher, Remplaçant·e trouvé·e, Déjà demandé, Dates flexibles, Tipee ; la personne qui remplace est
// « choisi·e » dans les messages de la fenêtre (jamais « désigné·e » ni « prévu·e »).
import { MAX_PREVENUS_PAR_SEANCE, type EtatSeance } from './seancesDemande';

/** Qui parle de qui. `pourMoi` = la personne absente est la personne connectée. */
export interface Voix { pourMoi: boolean; prenom: string; nomComplet: string }

const VOYELLE = /^[aeiouyhàâäéèêëîïôöùûüœ]/i;
/** « d'Alexia », « de Camille ». */
export function dePrenom(prenom: string): string {
  return (VOYELLE.test(prenom) ? "d'" : 'de ') + prenom;
}
/** « d'Alexia », ou « de la personne absente » tant qu'elle n'est pas choisie. */
export const dePersonne = (prenom: string): string => (prenom ? dePrenom(prenom) : 'de la personne absente');
/** « qu'Alexia Sestito », « que Camille Rochat » (Vérifiez … a bien fait sa demande). */
export function queNom(nom: string): string {
  return (VOYELLE.test(nom) ? "qu'" : 'que ') + nom;
}
/** Texte à la 2e personne pour soi, à la 3e pour un tiers : `{P}` = prénom (ou « la personne absente » tant qu'elle n'est pas choisie). */
export function voix(v: Voix, vous: string, tiers: string): string {
  return v.pourMoi ? vous : tiers.replace(/\{P\}/g, v.prenom || 'la personne absente');
}

/** « 1 jour », « 3 jours » ; `mots` = le pluriel s'il n'est pas « mot + s ». */
export const pluriel = (n: number, mot: string, mots = `${mot}s`): string => `${n} ${n > 1 ? mots : mot}`;
/** « AS » pour Alexia Sestito (pastille d'une personne). */
export const initiales = (p: { prenom: string; nom: string }): string => `${p.prenom[0] ?? ''}${p.nom[0] ?? ''}`.toUpperCase();
/** « 12,5 h » */
export const formatHeures = (h?: number): string => `${(h ?? 0).toLocaleString('fr-CH', { maximumFractionDigits: 1 })} h`;

export const LIBELLE_ETAT: Record<EtatSeance, string> = {
  rech: 'À rechercher',
  trouve: 'Remplaçant·e trouvé·e',
  deja: 'Déjà demandé',
  off: 'Pas de remplaçant·e à trouver',
};

/** Les trois choix de la fenêtre de séance, chacun avec ce qu'il fait. */
export const CHOIX_SEANCE: { etat: Exclude<EtatSeance, 'deja'>; vous: string; tiers: string }[] = [
  { etat: 'rech', vous: "Un message pour vos collègues est préparé à l'envoi de la demande.", tiers: "Un message pour les collègues est préparé à l'envoi de la demande." },
  { etat: 'trouve', vous: "Vous avez déjà quelqu'un : pas de message pour vos collègues, la personne choisie confirme dans l'application.", tiers: "{P} a déjà quelqu'un : pas de message pour les collègues, la personne choisie confirme dans l'application." },
  { etat: 'off', vous: "Aucune demande ne sera créée depuis cette page pour cette séance.", tiers: "Aucune demande ne sera créée depuis cette page pour cette séance." },
];

/**
 * Ce que reçoit une personne prévenue personnellement, tel qu'il lui sera écrit : le titre de la notification du serveur pour UNE séance
 * (server/utils/prevenus.ts::titreMessage). Prévenue pour plusieurs séances dans le même envoi, elle reçoit un seul message : « … pour N remplacements : dates ».
 */
export function phraseMessagePersonnel(v: Voix): string {
  return v.pourMoi
    ? `« ${v.nomComplet} pense à vous pour ce remplacement »`
    : `« L'équipe gestion pense à vous pour remplacer ${v.nomComplet || 'la personne absente'} »`;
}

/**
 * Ce que fait « Prévenir aussi personnellement », dit sur place : OÙ la personne le voit (la cloche de l'application, ni e-mail ni SMS), QUAND (à sa
 * prochaine ouverture de l'application), que cela s'ajoute au message pour tout le monde, et qui est proposé. Le message pour tout le monde, lui,
 * n'est jamais promis comme « parti » : tant qu'il n'est pas publié, il est « préparé » (DECISIONS §24).
 */
export function aidePrevenir(v: Voix): string {
  return `Les personnes cochées reçoivent en plus un message personnel dans l'application (la cloche) : ${phraseMessagePersonnel(v)}, avec le bouton « Je me propose ». Elles le voient à leur prochaine ouverture de l'application (ni e-mail, ni SMS). Cela s'ajoute au message ${v.pourMoi ? 'préparé pour vos collègues' : 'préparé pour les collègues'}, qui reste inchangé. Seules les personnes disponibles et ayant un compte sont proposées (${MAX_PREVENUS_PAR_SEANCE} au plus).`;
}

/** Ce que reçoit la personne choisie « Remplaçant·e trouvé·e », selon qu'elle a un compte et une adresse e-mail (le dire AU MOMENT du choix). */
export function phraseNotificationDesignation(p: { prenom: string; a_un_compte?: boolean; email: string | null }): string {
  if (p.a_un_compte === false) {
    return p.email
      ? `${p.prenom} n'utilise pas encore l'application : pensez à prévenir ${p.prenom} directement ; la confirmation se fera à sa première connexion.`
      : `${p.prenom} ne recevra aucune notification : pensez à prévenir ${p.prenom} directement.`;
  }
  return `${p.prenom} recevra une notification dans l'application et devra confirmer.`;
}
